/**
 * Settings → Audit Log — the tier offer's audit feature (Pro: 90-day
 * history; Enterprise: unlimited + CSV export; Free/Growth: upgrade notice).
 * Every row answers "who did what, when" from the always-on audit trail.
 */
import React, { useState } from 'react';
import { ClipboardDocumentListIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuditLogs, downloadAuditLogExport } from '../../services/auditLogs';
import FeatureUpgradeNotice from '../common/FeatureUpgradeNotice';
import { FEATURE, planHasFeature, planLimit } from './tiers/tierCatalog';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';


const formatWhen = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : '—';

/** SNAKE_CASE action → readable label ("COURSE_CREATED" → "Course created"). */
const humanize = (action: string): string => {
  const words = action.toLowerCase().split('_');
  return words.length
    ? words[0].charAt(0).toUpperCase() + words[0].slice(1) + ' ' + words.slice(1).join(' ')
    : action;
};

export default function AuditLogSettings() {
  const { theme } = useTheme();
  const { activeMembership } = useAuth();
  const plan = activeMembership?.tenant?.subscription_plan ?? 'free';
  const hasAudit = planHasFeature(FEATURE.auditLogs, plan);
  // Unlimited retention is the Enterprise line; Pro keeps 90 days.
  const isEnterprise = planLimit('auditLogRetentionDays', plan) === null && hasAudit;

  const [page, setPage] = useState(1);
  const [actionInput, setActionInput] = useState('');
  const actionFilter = useDebounce(actionInput, 300);
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, isError } = useAuditLogs(
    { page, action: actionFilter || undefined },
    hasAudit
  );

  if (!hasAudit) {
    return (
      <div>
        <h2 className="text-lg font-semibold text-text-primary mb-1">Audit Log</h2>
        <p className="text-sm text-text-secondary mb-6">
          Who did what, and when — across your whole workspace
        </p>
        <FeatureUpgradeNotice
          feature="Audit Log"
          description="Keep a reviewable trail of every important action in your workspace: courses published, learners added, points adjusted, settings changed."
          currentPlan={plan}
          icon={<ClipboardDocumentListIcon className="w-8 h-8" />}
        />
      </div>
    );
  }

  const logs = data?.logs ?? [];
  const pagination = data?.pagination;

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadAuditLogExport();
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-text-primary mb-1">Audit Log</h2>
          <p className="text-sm text-text-secondary">
            {data?.window_days
              ? `Showing the last ${data.window_days} days — Enterprise keeps unlimited history.`
              : 'Unlimited history for your whole workspace.'}
          </p>
        </div>
        {isEnterprise && (
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="btn btn-secondary rounded-full inline-flex items-center gap-2 active:scale-[0.97] disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        )}
      </div>

      <div className="mb-4 max-w-xs">
        <input
          type="text"
          value={actionInput}
          onChange={(e) => {
            setActionInput(e.target.value);
            setPage(1);
          }}
          placeholder="Filter by action (e.g. course, login)…"
          aria-label="Filter by action"
          className="w-full px-3.5 py-2.5 border border-border-primary rounded-lg bg-background-primary text-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-200"
        />
      </div>

      <phantom-ui {...phantomProps(theme === 'dark', isLoading)}>
        {isError ? (
          <p className="text-sm text-error-600 dark:text-error-400 py-8">
            Couldn't load the audit log. Please try again.
          </p>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-primary bg-grey-50 dark:bg-grey-800/50">
                    <th className="px-4 py-3 text-left body-small font-semibold text-text-secondary whitespace-nowrap">When</th>
                    <th className="px-4 py-3 text-left body-small font-semibold text-text-secondary">Who</th>
                    <th className="px-4 py-3 text-left body-small font-semibold text-text-secondary">Action</th>
                    <th className="px-4 py-3 text-left body-small font-semibold text-text-secondary">Resource</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 && !isLoading ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-10 text-center text-sm text-text-secondary">
                        No entries{actionFilter ? ' match this filter' : ' yet'}.
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr
                        key={log.id}
                        className="border-b border-border-primary/40 last:border-0"
                      >
                        <td className="px-4 py-3 body-small text-text-secondary whitespace-nowrap">
                          {formatWhen(log.created_at)}
                        </td>
                        <td className="px-4 py-3 body-small font-medium text-text-primary whitespace-nowrap">
                          {log.actor_name ?? 'System'}
                        </td>
                        <td className="px-4 py-3 body-small text-text-primary">
                          {humanize(log.action)}
                        </td>
                        <td className="px-4 py-3 body-small text-text-secondary">
                          {log.resource_type ?? '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination && pagination.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border-primary">
                <span className="body-small text-text-secondary">
                  Page {pagination.page} of {pagination.pages} · {pagination.total} entries
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={!pagination.has_prev}
                    className="btn btn-ghost btn-sm rounded-full disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!pagination.has_next}
                    className="btn btn-ghost btn-sm rounded-full disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </phantom-ui>
    </div>
  );
}
